import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    HttpTransportType,
    LogLevel,
} from "@microsoft/signalr";

const isSignalREnabled = () => true;

class SignalRService {
    private connection: HubConnection | null = null;

    isEnabled() {
        return isSignalREnabled();
    }

    async start(accessToken: string, companyId: string) {
        if (!isSignalREnabled()) {
            return;
        }

        if (
            this.connection?.state === HubConnectionState.Connected ||
            this.connection?.state === HubConnectionState.Connecting ||
            this.connection?.state === HubConnectionState.Reconnecting
        ) {
            return;
        }

        // skipNegotiation: bypass POST /signalr/negotiate while deploy CORS still
        // omits x-signalr-user-agent for *.centro-develop.com.
        this.connection = new HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_API_URL}/signalr`, {
                accessTokenFactory: () => accessToken,
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        this.connection.onclose(() => {
            console.log("SignalR disconnected");
        });

        this.connection.onreconnecting(() => {
            console.log("SignalR reconnecting...");
        });

        this.connection.onreconnected(async () => {
            console.log("SignalR reconnected");
            await this.joinCompanyGroup(companyId);
        });

        await this.connection.start();
        console.log("SignalR connected");
        await this.joinCompanyGroup(companyId);
    }

    async joinCompanyGroup(companyId: string) {
        if (!isSignalREnabled() || this.connection?.state !== HubConnectionState.Connected) {
            return;
        }

        try {
            await this.connection.invoke("JoinCompanyGroup", companyId);
            console.log("Joined company group:", companyId);
        } catch (e) {
            console.error(e);
        }
    }

    on(event: string, callback: (...args: any[]) => void) {
        if (!isSignalREnabled()) {
            return () => {};
        }

        this.connection?.on(event, callback);

        return () => {
            this.connection?.off(event, callback);
        };
    }

    off(event: string, callback?: (...args: any[]) => void) {
        if (!isSignalREnabled() || !this.connection) {
            return;
        }

        if (callback) {
            this.connection.off(event, callback);
        } else {
            this.connection.off(event);
        }
    }

    async stop() {
        if (!this.connection) {
            return;
        }

        try {
            if (this.connection.state !== HubConnectionState.Disconnected) {
                await this.connection.stop();
            }
        } catch (error) {
            console.error("SignalR stop error:", error);
        } finally {
            this.connection = null;
        }
    }

    getConnection() {
        return isSignalREnabled() ? this.connection : null;
    }
}

export const signalRService = new SignalRService();
